import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import DOMPurify from "dompurify";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ChevronRight, Search, Info, AlertCircle } from "lucide-react";
import SEO from "@/components/SEO";
import { KURSE_TEXT, LEKTIONEN_TEXT, gross } from "@/config/webseiteFakten";
import {
  MUSIK_KITA_PAKET_PRICE_EUR,
  SUBSCRIPTION_TIERS,
  TRIAL_PERIOD_DAYS,
  TRIAL_PRICE_EUR,
} from "@/config/subscriptionTiers";
import { formatEuro, getBundlePricingBySlug, type BundleTarif } from "@/config/trainingBundlePricing";

type FAQ = {
  id: string;
  kategorie: string;
  frage: string;
  antwort: string;
};

// Regex für Tabellen-Separator - Zeichen einzeln konkateniert, um Tailwind CSS Scanner zu umgehen
const DASH = String.fromCharCode(45); // -
const COLON = String.fromCharCode(58); // :
const PIPE = String.fromCharCode(124); // |
const TABLE_SEPARATOR_REGEX = new RegExp(
  '\\n\\|(.+)\\|\\n\\|[' + DASH + COLON + '\\s' + PIPE + ']+\\|\\n((?:\\|.+\\|\\n?)+)',
  'g'
);

// Helper function to convert markdown to HTML with XSS sanitization
const formatTextWithLinks = (text: string): string => {
  let formatted = text;
  
  // Tables (must be processed before other formatting)
  formatted = formatted.replace(TABLE_SEPARATOR_REGEX, (match, header, rows) => {
    const headers = header.split('|').map((h: string) => h.trim()).filter((h: string) => h);
    const rowLines = rows.trim().split('\n');
    
    let tableHTML = '<div class="overflow-x-auto my-4"><table class="min-w-full border border-border rounded-lg">';
    
    // Header row
    tableHTML += '<thead class="bg-muted/50"><tr>';
    headers.forEach((h: string) => {
      tableHTML += `<th class="border border-border px-4 py-3 text-left font-semibold text-foreground">${h}</th>`;
    });
    tableHTML += '</tr></thead>';
    
    // Body rows
    tableHTML += '<tbody>';
    rowLines.forEach((row: string) => {
      if (row.trim()) {
        const cells = row.split('|').map((c: string) => c.trim()).filter((c: string) => c);
        tableHTML += '<tr class="border-t border-border">';
        cells.forEach((cell: string) => {
          tableHTML += `<td class="border border-border px-4 py-3 text-foreground">${cell}</td>`;
        });
        tableHTML += '</tr>';
      }
    });
    tableHTML += '</tbody></table></div>';
    
    return tableHTML;
  });
  
  // Headers with different levels (must be processed before bold)
  formatted = formatted.replace(/^#### (.+)$/gm, '<h4 class="text-base font-semibold text-foreground mt-3 mb-2">$1</h4>');
  formatted = formatted.replace(/^### (.+)$/gm, '<h3 class="text-lg font-semibold text-foreground mt-4 mb-2">$1</h3>');
  formatted = formatted.replace(/^## (.+)$/gm, '<h2 class="text-xl font-bold text-foreground mt-5 mb-3">$1</h2>');
  formatted = formatted.replace(/^# (.+)$/gm, '<h1 class="text-2xl font-bold text-primary mt-6 mb-4">$1</h1>');
  
  // Links: [text](url)
  formatted = formatted.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-accent hover:underline font-medium">$1</a>');
  // Bold: **text**
  formatted = formatted.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold">$1</strong>');
  // Italic: *text*
  formatted = formatted.replace(/\*([^*]+)\*/g, '<em class="italic">$1</em>');
  
  // Sanitize output to prevent XSS attacks
  return DOMPurify.sanitize(formatted, {
    ALLOWED_TAGS: ['a', 'strong', 'em', 'h1', 'h2', 'h3', 'h4', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'div', 'ul', 'ol', 'li', 'hr', 'br', 'p', 'span'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class']
  });
};

const MITGLIED_MONAT = SUBSCRIPTION_TIERS.pro.pricing.monthly.price;
const MITGLIED_JAHR = SUBSCRIPTION_TIERS.pro.pricing.yearly.price;
/** Regulärer Preis einer Ausbildung aus der Anzeige-Config (dieselben Beträge wie die Kasse). */
const ausbildungspreis = (slug: string, tarif: BundleTarif): string => {
  const cents = getBundlePricingBySlug(slug)?.tarife[tarif]?.rechnung.amountCents;
  return cents ? formatEuro(cents) : "";
};
const FFM_PREIS = ausbildungspreis("fachkraft-musik", "standard");
const GITARRE_PREIS = ausbildungspreis("fachkraft-gitarre", "standard");
const MS_EXTERN = ausbildungspreis("fachkraft-musik-und-sprache", "extern");
const MS_MITGLIED = ausbildungspreis("fachkraft-musik-und-sprache", "mitglied");
const FFM_MITGLIED = ausbildungspreis("fachkraft-musik", "mitglied");
const GITARRE_MITGLIED = ausbildungspreis("fachkraft-gitarre", "mitglied");

// Fragen auf dem Stand vom 14.10.2026 (Sitzung „Aufräumen“, spaeter.md Punkt 37): heutige Tarife (Basis und
// Mitgliedschaft), Preise aus den Kassen, nur noch Links auf diese Seite. Die alten Fragen zu „Premium“, „Kreativ“,
// „Live-Mitglied“ und mit Links in die alte Akademie (Thinkific) stehen in der Git-Historie. Die Fragen gehen als
// FAQ-Daten an Google (SEO unten): Preise hier nur nennen, wenn sie in der Kasse genauso stehen.
const faqs: FAQ[] = [
  {
    id: "was-ist-musikonzept",
    kategorie: "Erste Schritte bei MusiKonzept",
    frage: "Was ist MusiKonzept und wie funktioniert es?",
    antwort: `MusiKonzept ist eine Online-Plattform für Musik mit Kindern – speziell für pädagogische Fachkräfte in Krippe, Kita, Tagespflege und Grundschule. Du brauchst keine musikalischen Vorkenntnisse.

---

## 🎯 Was bietet MusiKonzept?

• ${gross(KURSE_TEXT)} und ${LEKTIONEN_TEXT} zu Liedern, Rhythmus, Bewegung, Stimme, Gitarre, Ukulele & Co.
• Kreativpakete mit neuen Ideen jeden Monat (Lieder, Spielideen, Videos, PDFs)
• Live-Webinare und die Liederwerkstatt (Gitarre, Ukulele, Singen) über Zoom
• Ausbildungen mit Zertifikat, zum Beispiel Fachkraft für Musik im Elementarbereich
• Miko, unseren KI-Helfer, der Ideen vorschlägt und passende Inhalte findet
• Zertifikate und Teilnahmebescheinigungen für deinen Träger

---

## 🚀 So startest du

1. **Registrieren** – Lege dir ein [kostenloses Konto](/registrieren) an.
2. **Einloggen** – Melde dich unter [Login](/login) mit deiner E-Mail-Adresse an, im Browser oder in der App.
3. **Loslegen** – Wähle einen Kurs, ein Kreativpaket oder einen Termin aus und lege in deinem Tempo los.`
  },
  {
    id: "kostenlos-testen",
    kategorie: "Erste Schritte bei MusiKonzept",
    frage: "Kann ich MusiKonzept kostenlos testen?",
    antwort: `Ja, auf zwei Wegen:

## 🎓 Kostenloses Konto (Basis)

Mit einem kostenlosen Konto lernst du MusiKonzept in Ruhe kennen: ausgewählte Kurse und Lieder, kostenlose Webinare und Miko zum Ausprobieren. Es kostet nichts und endet nicht von selbst.

<div class="my-6"><a href="/registrieren" class="inline-flex items-center justify-center px-6 py-3 text-base font-semibold text-white bg-gradient-to-r from-primary to-accent rounded-lg hover:shadow-lg transition-all duration-300 hover:scale-105">Jetzt kostenlos registrieren →</a></div>

## 🎟️ Mitgliedschaft ${TRIAL_PERIOD_DAYS} Tage für ${TRIAL_PRICE_EUR} €

Die Mitgliedschaft kannst du ${TRIAL_PERIOD_DAYS} Tage lang für ${TRIAL_PRICE_EUR} € mit allen Inhalten testen. Kündigst du in dieser Zeit nicht, geht sie in die gewählte Laufzeit über: ${MITGLIED_MONAT} € im Monat oder ${MITGLIED_JAHR} € im Jahr.

<div class="my-6"><a href="/preise" class="inline-flex items-center justify-center px-6 py-3 text-base font-semibold text-white bg-gradient-to-r from-primary to-accent rounded-lg hover:shadow-lg transition-all duration-300 hover:scale-105">Zur Mitgliedschaft →</a></div>

Wer schon einmal Mitglied war, kann direkt wieder Mitglied werden, aber nicht noch einmal testen.`
  },
  {
    id: "mitgliedschaft",
    kategorie: "Mitgliedschaft",
    frage: "Was gehört zur Mitgliedschaft und was kostet sie?",
    antwort: `Die Mitgliedschaft kostet **${MITGLIED_MONAT} € im Monat** oder **${MITGLIED_JAHR} € im Jahr**. Dazu gehören:

• alle Online-Kurse und Kreativpakete
• die Liederwerkstatt live mit Aufzeichnungen
• die Themenwebinare ohne zusätzliche Kosten
• die Community
• Zertifikate für deine Kurse
• Miko, unser KI-Helfer
• bis zu zwei weitere Personen, die du in dein Team einlädst

Für Ausbildungen zahlst du als Mitglied einen günstigeren Preis (siehe „Ausbildungen“). Alle Details stehen auf der Seite [Preise](/preise).`
  },
  {
    id: "kuendigen",
    kategorie: "Mitgliedschaft",
    frage: "Wie kann ich kündigen oder widerrufen?",
    antwort: `**Kündigen:** In deinem Bereich unter [Mein Account](/mein-bereich/einstellungen) → „Abo & Rechnungen“ → „Abo kündigen“. Kommst du nicht mehr in dein Konto, schreib uns an info@musikonzept.de.

Die monatliche Mitgliedschaft ist monatlich kündbar. Die jährliche verlängert sich um ein Jahr, wenn sie nicht bis vier Wochen vor Ablauf gekündigt wird. Die Testphase kannst du innerhalb der ${TRIAL_PERIOD_DAYS} Tage kündigen.

**Widerrufen:** Innerhalb von 14 Tagen nach Vertragsschluss kannst du ohne Angabe von Gründen widerrufen, über [Vertrag widerrufen](/vertrag-widerrufen). Die Widerrufsbelehrung und das Muster-Formular findest du unter [Widerruf](/widerruf).`
  },
  {
    id: "rechnungen-finden",
    kategorie: "Mitgliedschaft",
    frage: "Wo finde ich meine Rechnungen?",
    antwort: `Melde dich an und öffne in deinem Bereich die **Einstellungen**. Unter **„Käufe & Rechnungen“** findest du alle Rechnungen zum Herunterladen, auch die deiner Ausbildung. Die Rechnungen deiner Mitgliedschaft findest du außerdem über „Mein Abo verwalten“ im Kundenportal.

Die Kosten für MusiKonzept kannst du in der Regel als Fortbildung steuerlich absetzen.`
  },
  {
    id: "team-einladen",
    kategorie: "Mitgliedschaft",
    frage: "Kann ich Kolleginnen in meine Mitgliedschaft einladen?",
    antwort: `Ja. Mit deiner Mitgliedschaft kannst du bis zu zwei weitere Personen einladen. Öffne dazu in deinem Bereich die **Einstellungen** und dort den Reiter **„Team“**. Deine Kolleginnen bekommen eine Einladung per E-Mail und legen sich ein eigenes Konto an.

Das geht nur mit einer eigenen Mitgliedschaft, nicht mit dem Jahr Mitgliedschaft, das zu einer Ausbildung gehört.`
  },
  {
    id: "webinare-musikonzept",
    kategorie: "Webinare und Liederwerkstatt",
    frage: "Wie nehme ich an einem Webinar teil?",
    antwort: `Unsere Webinare sind Live-Fortbildungen über Zoom. Alle Termine findest du unter [Termine](/termine).

• **Kostenlose Webinare** sind für alle frei, du meldest dich auf der Terminseite an.
• **Themenwebinare** sind kostenpflichtig; mit Mitgliedschaft meldest du dich dort kostenlos an.
• **Ausbildungs-Webinare** sind für die Teilnehmenden der jeweiligen Ausbildung.

Nach der Anmeldung bekommst du eine Bestätigung per E-Mail. Den Zoom-Link findest du in deinem Bereich unter **„Termine“**. Zu vielen Webinaren gibt es danach eine Aufzeichnung und eine Teilnahmebescheinigung.`
  },
  {
    id: "liederwerkstatt",
    kategorie: "Webinare und Liederwerkstatt",
    frage: "Was ist die Liederwerkstatt und wie mache ich mit?",
    antwort: `Die **Liederwerkstatt** ist unsere Online-Musikschule über Zoom. Du lernst, Kinderlieder zu begleiten, ohne Noten lesen zu müssen:

• **gitarrissimo®** – Gitarre
• **Ukulissimo** – Ukulele
• **Vokalissimo** – Singen

**Mit Mitgliedschaft** ist die Liederwerkstatt enthalten: Melde dich unter [Termine](/termine) kostenlos an, den Zoom-Link findest du danach in deinem Bereich unter „Termine“. Die Aufzeichnungen und das Liedmaterial liegen im Kurs zum jeweiligen Fach.

**Ohne Mitgliedschaft** buchst du einzelne Termine über die Terminseite.

Für Gitarre oder Ukulele brauchst du ein Instrument und ein Stimmgerät (eine kostenlose App reicht), für Vokalissimo nur deine Stimme.`
  },
  {
    id: "kreativpaket",
    kategorie: "Kurse und Inhalte",
    frage: "Was ist das Kreativpaket?",
    antwort: `Das Kreativpaket erscheint jeden Monat neu: ein neues Kinderlied mit Video, Noten und Playback, dazu Spielideen, Bewegungsimpulse und PDFs. Mit Mitgliedschaft sind alle Kreativpakete enthalten; ohne Mitgliedschaft kannst du einzelne Pakete kaufen. Alle Kreativpakete findest du unter [Kurse](/kurse?kategorie=kreativpakete).`
  },
  {
    id: "passende-inhalte-finden",
    kategorie: "Kurse und Inhalte",
    frage: "Wie finde ich passende Inhalte?",
    antwort: `Unter [Kurse](/kurse) siehst du alle Kurse, sortiert nach Kategorien wie Kreativpakete, Online-Fortbildungen, Webinar-Aufzeichnungen, Gitarre & Ukulele und Liederwerkstatt. In deinem Bereich findest du über die **Suche** Lieder, Spielideen und Videos zu einem Stichwort, zum Beispiel „Herbst“, „Trommel“ oder „Morgenkreis“. Miko hilft dir dabei, die passende Stelle in einem Video zu finden.

Wenn du unsicher bist, schreib uns über das [Kontaktformular](/kontakt).`
  },
  {
    id: "zertifikate",
    kategorie: "Kurse und Inhalte",
    frage: "Wo finde ich meine Zertifikate?",
    antwort: `Für viele Kurse bekommst du eine Teilnahmebescheinigung, sobald du alle Lektionen abgeschlossen hast (Fortschritt 100 %). Du findest sie in deinem Bereich unter **„Zertifikate“** zum Herunterladen.`
  },
  {
    id: "ausbildungen-musikonzept",
    kategorie: "Ausbildungen",
    frage: "Welche Ausbildungen gibt es?",
    antwort: `Alle Ausbildungen laufen online und berufsbegleitend, in deinem Tempo, mit Live-Terminen und Zertifikat.

## 🎼 Fachkraft für Musik im Elementarbereich

Staatlich zugelassener Fernlehrgang (ZFU-Nr. 7432522c), Umfang 330 Stunden. **${FFM_PREIS}** (**${FFM_MITGLIED}** für Mitglieder), auch in Raten; ein Jahr Mitgliedschaft ist inklusive. Bist du schon Mitglied, ruht dein eigenes Abo ab der Buchung ein Jahr. [Zur Ausbildung](/ausbildung/fachkraft-musik)

## 🗣️ Fachkraft für Musik und Sprache im Elementarbereich

Vier Kurse zu Sprachbildung mit Musik, rund 50 Stunden. Einstieg jederzeit, dein Zugang ist mit der Anmeldung sofort frei und läuft nicht ab. Nach der Abschlussprüfung und deinem kurzen Feedback bekommst du dein Zertifikat. **${MS_EXTERN}** (**${MS_MITGLIED}** für Mitglieder), auch in Raten. Eine Mitgliedschaft ist nicht enthalten. [Zur Ausbildung](/ausbildung/fachkraft-musik-und-sprache)

## 🎸 Der große Kita-Gitarrenkurs

Liedbegleitung auf der Gitarre für Krippe und Kita, auch ohne Vorkenntnisse, mit MusiKonzept-Zertifikat. **${GITARRE_PREIS}** (**${GITARRE_MITGLIED}** für Mitglieder), auch in Raten; ein Jahr Mitgliedschaft ist inklusive. Bist du schon Mitglied, ruht dein eigenes Abo ab der Buchung ein Jahr. [Zur Ausbildung](/ausbildung/fachkraft-gitarre)

Ob dein Bundesland die Ausbildung fördert, siehst du unter [Fördermöglichkeiten](/foerdermoeglichkeiten). Zu jeder Ausbildung gibt es ein kostenloses Info-Webinar, die Termine stehen unter [Termine](/termine).`
  },
  {
    id: "musik-kita-zertifizierung",
    kategorie: "Musik-Kita",
    frage: "Was ist das Musik-Kita-Paket?",
    antwort: `Das **Musik-Kita-Paket** ist der Zugang für eure ganze Einrichtung: das Team und die Eltern bekommen Zugang zu allen Kursen und Materialien, dazu Live-Unterricht, ein Buchpaket und den Weg zum Musik-Kita-Gütesiegel mit Eintrag im [Musik-Kita-Verzeichnis](/musik-kita-verzeichnis).

• **${MUSIK_KITA_PAKET_PRICE_EUR} € im Jahr** für die ganze Einrichtung
• 2 Wochen kostenlos testen
• Rechnung an Kita oder Träger möglich

Für das Siegel absolviert eine Person aus dem Team drei kurze Online-Fortbildungen. Alles Weitere steht auf der Seite [Musik-Kita](/musik-kita).`
  },
  {
    id: "kontakt",
    kategorie: "Erste Schritte bei MusiKonzept",
    frage: "Wie erreiche ich euch?",
    antwort: `Schreib uns über das [Kontaktformular](/kontakt) oder an info@musikonzept.de. Bist du angemeldet, kannst du in deinem Bereich unter **„Hilfe & Support“** auch ein Ticket schreiben; dort findest du außerdem Anleitungen zu vielen Fragen.`
  },
];

const Hilfe = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedKategorie, setSelectedKategorie] = useState<string>(
    searchParams.get('kategorie') || "alle"
  );
  const [selectedFaq, setSelectedFaq] = useState<FAQ | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const kategorien = ["alle", ...Array.from(new Set(faqs.map(f => f.kategorie)))];

  useEffect(() => {
    const kategorie = searchParams.get('kategorie');
    if (kategorie) {
      setSelectedKategorie(kategorie);
    }
  }, [searchParams]);

  useEffect(() => {
    const faqId = searchParams.get('faq');
    if (faqId) {
      const faq = faqs.find(f => f.id === faqId);
      if (faq) {
        setSelectedFaq(faq);
      }
    }
  }, [searchParams]);

  const filteredFaqs = faqs.filter((faq) => {
    const kategorieMatch = selectedKategorie === "alle" || faq.kategorie === selectedKategorie;
    if (!searchQuery) return kategorieMatch;
    
    const query = searchQuery.toLowerCase();
    return kategorieMatch && (
      faq.frage.toLowerCase().includes(query) ||
      faq.antwort.toLowerCase().includes(query) ||
      faq.kategorie.toLowerCase().includes(query)
    );
  });

  const faqsByKategorie = kategorien
    .filter(k => k !== "alle")
    .map(kategorie => ({
      kategorie,
      faqs: filteredFaqs.filter(f => f.kategorie === kategorie)
    }))
    .filter(group => group.faqs.length > 0);

  return (
    <div className="min-h-screen">
      <SEO
        title="Hilfe & FAQ | MusiKonzept"
        description="Antworten auf häufige Fragen rund um Mitgliedschaft, Ausbildungen, Zertifikate und technische Themen bei MusiKonzept."
        path="/hilfe"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.slice(0, 20).map((f) => ({
            "@type": "Question",
            name: f.frage,
            acceptedAnswer: {
              "@type": "Answer",
              text: f.antwort.replace(/\s+/g, " ").slice(0, 1000),
            },
          })),
        }}
      />
      <Header />
      <main className="pt-20">
        {/* Hero Section with Orange Gradient */}
        <div className="bg-gradient-to-br from-accent/20 via-accent/10 to-background py-16">
          <div className="container mx-auto px-6">
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
                Wie können wir helfen?
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Durchsuche unsere häufig gestellten Fragen oder nutze die Suche
              </p>
            </div>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto mb-8">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground h-5 w-5" />
                <Input
                  placeholder="Suchen Sie nach Themen, Fragen..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-14 text-lg bg-card shadow-lg border-2 focus-visible:ring-2"
                />
              </div>
            </div>

            {/* Category Filter - Mobile Tabs */}
            <div className="md:hidden mb-8">
              <Tabs value={selectedKategorie} onValueChange={(value) => {
                setSelectedKategorie(value);
                if (value === "alle") {
                  searchParams.delete('kategorie');
                } else {
                  searchParams.set('kategorie', value);
                }
                setSearchParams(searchParams);
              }}>
                <TabsList className="w-full justify-start overflow-x-auto flex-nowrap h-auto bg-card/50">
                  {kategorien.map((kategorie) => (
                    <TabsTrigger key={kategorie} value={kategorie} className="whitespace-nowrap">
                      {kategorie === "alle" ? "Alle Kategorien" : kategorie}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </div>

            {/* Category Filter - Desktop Pills */}
            <div className="hidden md:flex justify-center gap-3 flex-wrap">
              {kategorien.map((kategorie) => (
                <button
                  key={kategorie}
                  onClick={() => {
                    setSelectedKategorie(kategorie);
                    if (kategorie === "alle") {
                      searchParams.delete('kategorie');
                    } else {
                      searchParams.set('kategorie', kategorie);
                    }
                    setSearchParams(searchParams);
                  }}
                  className={`px-6 py-2 rounded-full font-medium transition-all ${
                    selectedKategorie === kategorie
                      ? 'bg-accent text-accent-foreground shadow-md'
                      : 'bg-card hover:bg-card/80 text-foreground shadow-sm'
                  }`}
                >
                  {kategorie === "alle" ? "Alle Kategorien" : kategorie}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* FAQ Cards */}
        <div className="container mx-auto px-6 py-12">
          {filteredFaqs.length === 0 ? (
            <Card className="max-w-2xl mx-auto">
              <CardContent className="text-center py-12">
                <Search className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">Keine Ergebnisse gefunden</h3>
                <p className="text-muted-foreground">
                  Versuchen Sie es mit anderen Suchbegriffen oder kontaktieren Sie uns direkt.
                </p>
              </CardContent>
            </Card>
          ) : selectedKategorie === "alle" ? (
            // Group by category when "alle" is selected
            <div className="space-y-12 max-w-5xl mx-auto">
              {faqsByKategorie.map((group) => (
                <div key={group.kategorie}>
                  <h2 className="text-2xl font-bold text-foreground mb-6">
                    {group.kategorie}
                  </h2>
                  <div className="grid gap-4 md:grid-cols-2">
                    {group.faqs.map((faq) => (
                      <Card
                        key={faq.id}
                        className="cursor-pointer hover:shadow-lg transition-all duration-300 group"
                        onClick={() => {
                          setSelectedFaq(faq);
                          setSearchParams({ faq: faq.id });
                        }}
                      >
                        <CardHeader>
                          <div className="flex items-start justify-between gap-4">
                            <CardTitle className="text-lg leading-tight group-hover:text-accent transition-colors">
                              {faq.frage}
                            </CardTitle>
                            <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0 group-hover:text-accent group-hover:translate-x-1 transition-all" />
                          </div>
                        </CardHeader>
                      </Card>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Single category view
            <div className="max-w-5xl mx-auto">
              <h2 className="text-2xl font-bold text-foreground mb-6">
                {selectedKategorie}
              </h2>
              <div className="grid gap-4 md:grid-cols-2">
                {filteredFaqs.map((faq) => (
                  <Card
                    key={faq.id}
                    className="cursor-pointer hover:shadow-lg transition-all duration-300 group"
                    onClick={() => {
                      setSelectedFaq(faq);
                      setSearchParams({ faq: faq.id });
                    }}
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between gap-4">
                        <CardTitle className="text-lg leading-tight group-hover:text-accent transition-colors">
                          {faq.frage}
                        </CardTitle>
                        <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0 group-hover:text-accent group-hover:translate-x-1 transition-all" />
                      </div>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* FAQ Detail Dialog */}
      <Dialog open={!!selectedFaq} onOpenChange={(open) => {
        if (!open) {
          setSelectedFaq(null);
          searchParams.delete('faq');
          setSearchParams(searchParams);
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          {selectedFaq && (
            <>
              <DialogHeader>
                <div className="mb-3">
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-accent/10 text-accent">
                    {selectedFaq.kategorie}
                  </span>
                </div>
                <DialogTitle className="text-3xl leading-tight pr-8">{selectedFaq.frage}</DialogTitle>
              </DialogHeader>
              <div className="mt-6 space-y-6">
                {selectedFaq.antwort.split('\n\n').map((section, index) => {
                  // Check for HTML content (info boxes, iframes, etc.)
                  if (section.trim().startsWith('<div')) {
                    return (
                      <div key={index} dangerouslySetInnerHTML={{ __html: section }} />
                    );
                  }
                  
                  // Check for horizontal rule
                  if (section.trim() === '---') {
                    return <hr key={index} className="border-t-2 border-border/30 my-6" />;
                  }
                  
                  // Check if section is a heading (starts with emoji)
                  if (section.match(/^[🎯🔎📂💡🚀🔍✨]/)) {
                    const lines = section.split('\n');
                    const heading = lines[0];
                    const content = lines.slice(1);
                    
                    return (
                      <div key={index} className="space-y-3">
                        <h3 className="text-xl font-bold text-foreground flex items-center gap-2">
                          {heading}
                        </h3>
                        {content.length > 0 && (
                          <div className="space-y-2 pl-1">
                            {content.map((line, lineIndex) => {
                              if (line.startsWith('•')) {
                                const text = line.substring(1).trim();
                                return (
                                  <div key={lineIndex} className="flex items-start gap-3">
                                    <span className="text-accent mt-1">•</span>
                                    <span className="text-foreground flex-1" dangerouslySetInnerHTML={{ __html: formatTextWithLinks(text) }} />
                                  </div>
                                );
                              } else if (line.match(/^\d+\./)) {
                                // Numbered list - check for nested bullets
                                const match = line.match(/^(\d+)\.\s*(.+)$/);
                                if (match) {
                                  const number = match[1];
                                  const text = match[2];
                                  
                                  // Check if next lines are indented bullets
                                  let nextLineIndex = lineIndex + 1;
                                  const nestedBullets = [];
                                  while (nextLineIndex < content.length && content[nextLineIndex].match(/^\s+•/)) {
                                    nestedBullets.push(content[nextLineIndex].trim().substring(1).trim());
                                    nextLineIndex++;
                                  }
                                  
                                  return (
                                    <div key={lineIndex} className="flex items-start gap-3">
                                      <span className="font-bold text-accent">{number}.</span>
                                      <div className="flex-1">
                                        <span className="text-foreground" dangerouslySetInnerHTML={{ __html: formatTextWithLinks(text) }} />
                                         {nestedBullets.length > 0 && (
                                          <div className="mt-2 space-y-1">
                                            {nestedBullets.map((bullet, idx) => (
                                              <div key={idx} className="flex items-start gap-3 ml-1">
                                                <span className="text-accent mt-1">•</span>
                                                <span className="text-foreground flex-1" dangerouslySetInnerHTML={{ __html: formatTextWithLinks(bullet) }} />
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                } else {
                                  return (
                                    <div key={lineIndex} className="flex items-start gap-3">
                                      <span className="text-foreground" dangerouslySetInnerHTML={{ __html: formatTextWithLinks(line) }} />
                                    </div>
                                  );
                                }
                              } else if (line.trim() && !line.match(/^\s+•/)) {
                                return (
                                  <p key={lineIndex} className="text-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: formatTextWithLinks(line) }} />
                                );
                              }
                              return null;
                            })}
                          </div>
                        )}
                      </div>
                    );
                  }
                  
                  // Regular paragraph
                  if (section.trim()) {
                    const lines = section.split('\n');
                    return (
                      <div key={index} className="space-y-2">
                        {lines.map((line, lineIndex) => {
                          if (line.startsWith('•')) {
                            const text = line.substring(1).trim();
                            return (
                              <div key={lineIndex} className="flex items-start gap-3">
                                <span className="text-accent mt-1">•</span>
                                <span className="text-foreground flex-1" dangerouslySetInnerHTML={{ __html: formatTextWithLinks(text) }} />
                              </div>
                            );
                          } else if (line.trim()) {
                            return (
                              <p key={lineIndex} className="text-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: formatTextWithLinks(line) }} />
                            );
                          }
                          return null;
                        })}
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default Hilfe;
